/** Axios/backend response body shape */
import {
  getNumberDisplayDecimals,
  NUMBER_DISPLAY_LOCALE,
} from '@/utils/numberDisplayFormatPrefs'

const getResponseData = (err: unknown): { message?: string | string[]; statusCode?: number } | null => {
  const ax = err as { response?: { data?: unknown } }
  return (ax?.response?.data as { message?: string | string[]; statusCode?: number }) ?? null
}

function isBlankMessage(value: string): boolean {
  const s = value.trim()
  return !s || s === 'undefined' || s === 'null' || s === '[object Object]'
}

/** Chuỗi đọc được từ message API: string, mảng validation, hoặc object class-validator. */
function textOfMessage(item: unknown): string {
  if (item == null) return ''
  if (typeof item === 'string') return isBlankMessage(item) ? '' : item.trim()
  if (typeof item === 'number' || typeof item === 'boolean') return String(item)
  if (Array.isArray(item)) {
    return item.map(textOfMessage).filter(Boolean).join(' | ')
  }
  if (typeof item === 'object') {
    const row = item as Record<string, unknown>
    const parts: string[] = []
    if (row.constraints && typeof row.constraints === 'object') {
      for (const value of Object.values(row.constraints as Record<string, unknown>)) {
        const text = textOfMessage(value)
        if (text) parts.push(text)
      }
    }
    if (Array.isArray(row.children) && row.children.length > 0) {
      const nested = textOfMessage(row.children)
      if (nested) parts.push(nested)
    }
    if (parts.length === 0 && row.message != null) {
      const text = textOfMessage(row.message)
      if (text) parts.push(text)
    }
    return parts.join(' | ')
  }
  return ''
}

function messageFromBody(src: { message?: unknown; statusCode?: number } | null | undefined): string {
  if (!src) return ''
  if (src.statusCode === 403) return '403 - Bạn không có quyền'
  return textOfMessage(src.message)
}

export const convertErrMessage = (err: unknown): string => {
  const fromAxios = messageFromBody(getResponseData(err))
  if (fromAxios) return fromAxios
  return messageFromBody(err as { message?: unknown; statusCode?: number })
}

export function handleError(err: unknown) {
  const msg = convertErrMessage(err)
  if (msg) console.error(msg)
}

import { notificationService } from '@/services/notificationService'

export type MessageType = 'success' | 'error' | 'info' | 'warning'

/** Chuẩn hóa nội dung toast. Mảng/object lỗi API không được thành chữ "undefined". */
export function normalizeToastMessage(message: unknown): string {
  if (typeof message === 'string') return isBlankMessage(message) ? '' : message.trim()
  return textOfMessage(message)
}

export function showMessage(
  type: MessageType,
  message: unknown,
  options?: { duration?: number; preventDuplicate?: boolean },
) {
  const text = normalizeToastMessage(message) || (type === 'error' ? 'Đã xảy ra lỗi' : '')
  if (!text) return
  try {
    notificationService.show(text, type, options)
  } catch {
    console[type === 'error' ? 'error' : 'info'](text)
  }
}

export function handleChangePaging(params: {
  history: { push: (path: string) => void }
  pathname: string
  search: string
  page: number
  pageSize?: number
  /** P14: optional whitelist pageSize options — clamp về option gần nhất nếu ngoài range. */
  allowedPageSizes?: readonly number[]
}) {
  const { history, pathname, search, page, pageSize, allowedPageSizes } = params
  const searchParams = new URLSearchParams(search || '')
  // P14: clamp page >= 1 + integer (chặn NaN, 0, âm, float).
  const clampedPage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1
  searchParams.set('page', String(clampedPage))
  if (pageSize !== undefined) {
    searchParams.set('pageSize', String(clampTablePageSize(pageSize, allowedPageSizes)))
  }
  history.push(`${pathname}?${searchParams.toString()}`)
}

const DEFAULT_TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

function stripLeadingQuestion(search: string) {
  return search.startsWith('?') ? search.slice(1) : search
}

export function clampTablePageSize(raw: number, allowed: readonly number[] = DEFAULT_TABLE_PAGE_SIZE_OPTIONS): number {
  const uniq = Array.from(new Set(allowed.filter((n) => Number.isFinite(n) && n > 0))).sort((a, b) => a - b)
  if (uniq.length === 0) return Number.isFinite(raw) && raw > 0 ? raw : 10
  const n = Number.isFinite(raw) && raw > 0 ? raw : uniq[0]
  if (uniq.includes(n)) return n
  const min = uniq[0]
  const max = uniq[uniq.length - 1]
  if (n < min) return min
  if (n > max) return max
  let best = min
  let bestDist = Math.abs(n - min)
  for (const opt of uniq) {
    const dist = Math.abs(n - opt)
    if (dist < bestDist) {
      best = opt
      bestDist = dist
    }
  }
  return best
}

/**
 * Chuẩn hoá `page` + `pageSize` trong querystring để khớp options phân trang của `TableList`.
 * Trả về querystring **không** có dấu `?` đầu dòng.
 */
export function normalizeTablePagingSearchString(
  search: string,
  params?: { defaultPageSize?: number; allowedPageSizes?: readonly number[] },
): string {
  const defaultPageSize = params?.defaultPageSize ?? 25
  const allowed = params?.allowedPageSizes ?? DEFAULT_TABLE_PAGE_SIZE_OPTIONS
  const parsed = new URLSearchParams(stripLeadingQuestion(search || ''))

  const pageRaw = Number(parsed.get('page') ?? '1')
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? Math.floor(pageRaw) : 1
  parsed.set('page', String(page))

  const pageSizeRaw = parsed.get('pageSize')
  const pageSizeNum = pageSizeRaw == null || pageSizeRaw === '' ? defaultPageSize : Number(pageSizeRaw)
  const normalizedPageSize = clampTablePageSize(
    Number.isFinite(pageSizeNum) && pageSizeNum > 0 ? pageSizeNum : defaultPageSize,
    allowed,
  )
  parsed.set('pageSize', String(normalizedPageSize))

  return parsed.toString()
}

export function replaceUrlSearchIfChanged(nextSearch: string) {
  if (typeof window === 'undefined') return
  const current = stripLeadingQuestion(window.location.search || '')
  if (current === nextSearch) return
  const { pathname, hash } = window.location
  window.history.replaceState(window.history.state, '', `${pathname}?${nextSearch}${hash}`)
}

export const getFullName = (firstname: string, lastName: string) => {
  return (lastName || '') + `${firstname && lastName ? ' ' : ''}` + (firstname || '')
}

export function isEmailValid(email: string) {
  // let re = /^[a-zA-Z0-9_\\.\\+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-\\.]+$/
  const re = /^[\w-\\.]+@([\w-]+\.)+[\w-]{2,}$/
  return re.test(email)
}

export function isOTPValid(otp: string) {
  const re = /^(\d(\s+)?){6}$/
  return re.test(otp)
}

export const isURL = (url: string) => {
  const expression =
    /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi
  const regex = new RegExp(expression)
  return url.match(regex)
}

/**
 * Converts cubic centimeters (cm³) to cubic meters (m³).
 * @param cm3 - The volume in cubic centimeters.
 * @returns The volume in cubic meters.
 */
export function cm3ToM3(dimensionInCm3: number): number {
  // return dimensionInCm3 * 1e-6
  // return Math.round(dimensionInCm3 * 1e-6 * 1000) / 1000
  return parseFloat((dimensionInCm3 * 1e-6).toFixed(6))
}

/**
 * Formats a number with dots as thousand separators.
 * @param {number} num - The number to format.
 * @returns {string} - The formatted string with dots as thousand separators.
 */
export function formatNumber(num: number | string): string {
  return formatVnNumber(num)
}

/**
 * Parse số dán từ Excel / ô nhập (ưu tiên locale VN):
 * - `3.224.000` / `8.160` → dấu chấm là hàng nghìn
 * - `1.234,56` → chấm nghìn, phẩy thập phân
 * - `1,234.56` → phẩy nghìn, chấm thập phân
 * - `1,1234` → phẩy thập phân (VN)
 */
export function parseExcelLocaleNumber(value: number | string | null | undefined): number | null {
  if (value == null) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const raw = String(value).trim().replace(/\s/g, '')
  if (raw === '' || raw === '-') return null

  const hasDot = raw.includes('.')
  const hasComma = raw.includes(',')

  if (hasDot && hasComma) {
    if (raw.lastIndexOf(',') > raw.lastIndexOf('.')) {
      const n = Number(raw.replace(/\./g, '').replace(',', '.'))
      return Number.isFinite(n) ? n : null
    }
    const n = Number(raw.replace(/,/g, ''))
    return Number.isFinite(n) ? n : null
  }

  if (hasComma && !hasDot) {
    const parts = raw.split(',')
    if (parts.length > 2 && parts.every((p) => /^\d+$/.test(p))) {
      const n = Number(parts.join(''))
      return Number.isFinite(n) ? n : null
    }
    if (parts.length === 2 && /^\d*$/.test(parts[0]) && /^\d*$/.test(parts[1])) {
      if (parts[0] === '' && parts[1] === '') return null
      const n = Number(`${parts[0] || '0'}.${parts[1] || '0'}`)
      return Number.isFinite(n) ? n : null
    }
    const n = Number(raw.replace(/,/g, ''))
    return Number.isFinite(n) ? n : null
  }

  if (hasDot) {
    const parts = raw.split('.')
    const allNumeric = parts.every((p) => /^\d+$/.test(p))
    if (!allNumeric) {
      const n = Number(raw)
      return Number.isFinite(n) ? n : null
    }
    if (parts.length > 2) {
      const n = Number(parts.join(''))
      return Number.isFinite(n) ? n : null
    }
    // `1.234` / `8.160` (đúng 3 số sau chấm) → dấu nghìn VN.
    // `0.016` / `.160` là thập phân US — không gộp thành 16 / 160
    // (tránh tỷ lệ quy đổi 0.015748 → DB 0.016 → UI hiểu 16).
    if (parts.length === 2 && parts[1].length === 3) {
      if (parts[0] === '' || /^0+$/.test(parts[0])) {
        const n = Number(raw)
        return Number.isFinite(n) ? n : null
      }
      const n = Number(parts[0] + parts[1])
      return Number.isFinite(n) ? n : null
    }
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  }

  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

/** Parse ô nhập số — hỗ trợ VN (`.` nghìn, `,` thập phân) và US. */
export function parseNumberInput(value: number | string | null | undefined): number | null {
  return parseExcelLocaleNumber(value)
}

/**
 * Số làm tròn về 0 ở độ chính xác hiển thị → trả đúng `0` (tránh UI hiện «-0»
 * với `-0` hoặc số âm rất nhỏ do sai số float, vd. tồn kho -0.0001).
 */
export function normalizeNegativeZero(value: number, maxDecimals = 3): number {
  if (!Number.isFinite(value)) return value
  const factor = 10 ** Math.max(0, Math.floor(maxDecimals))
  return Math.round(Math.abs(value) * factor) === 0 ? 0 : value
}

/**
 * Format hiển thị số theo `NUMBER_DISPLAY_LOCALE` (vi-VN: `.` nghìn, `,` thập phân).
 * Số chữ số thập phân theo Cài đặt Format số (mặc định `general`), bỏ 0 thừa.
 */
export function formatVnNumber(
  value: number | string | null | undefined,
  maxDecimals?: number,
): string {
  const n = parseNumberInput(value)
  if (n == null) return ''
  const decimals = Math.max(
    0,
    Math.floor(maxDecimals ?? getNumberDisplayDecimals('general')),
  )
  return new Intl.NumberFormat(NUMBER_DISPLAY_LOCALE, {
    maximumFractionDigits: decimals,
  }).format(normalizeNegativeZero(n, decimals))
}

/**
 * Số theo Cài đặt → Format số.
 * Số lẻ từ cấu hình người dùng; dấu nghìn/thập phân luôn `NUMBER_DISPLAY_LOCALE` (vi-VN).
 */
export function formatSoCaiDat(
  value: number | string | null | undefined,
  kind: 'general' | 'volume' | 'weight' = 'general',
): string {
  return formatVnNumber(value, getNumberDisplayDecimals(kind))
}

export function formatCm3ToM3(cm3?: number | null) {
  if (cm3) return formatNumber(cm3ToM3(cm3))
  return ''
}

/**
 * Format tiền — chỉ số, không kèm ₫/VND/$/¥.
 * Số lẻ theo Cài đặt Format số (vnd/usd/rmb); dấu ngăn cách = `NUMBER_DISPLAY_LOCALE`.
 */
export const formatPrice = (value: number | string, currency?: string): string => {
  if (!value && value !== 0) return '0' // Handle undefined/null/NaN/''
  const num = Number(value)
  // P13: defensive cho non-numeric string ('abc' → NaN).
  if (!Number.isFinite(num)) return '0'
  const code = (currency || 'VND').toUpperCase()
  const prefKey =
    code === 'USD' ? 'usd' : code === 'CNY' || code === 'RMB' ? 'rmb' : 'vnd'
  const decimals = getNumberDisplayDecimals(prefKey)
  const n = normalizeNegativeZero(num, decimals)
  // VND: cố định đúng số lẻ Cài đặt (thường 0). Ngoại tệ: tối đa, bỏ 0 thừa.
  if (prefKey === 'vnd') {
    return new Intl.NumberFormat(NUMBER_DISPLAY_LOCALE, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(n)
  }
  return new Intl.NumberFormat(NUMBER_DISPLAY_LOCALE, {
    maximumFractionDigits: decimals,
  }).format(n)
}
/** Parse an toàn chuỗi số (thường là Decimal.toString từ BE) về number. NaN/Infinity → 0. (P8 — DRY, dùng chung 4 card + page) */
export const toAmount = (raw: string | number | undefined | null): number => {
  const n = Number(raw ?? 0)
  return Number.isFinite(n) ? n : 0
}
export const convertResourceToLabel = (resource: string) => {
  const RESOURCE_TO_LABEL = {
    orders: 'Đơn hàng',
    shipments: 'Lô hàng',
    users: 'Người dùng',
    customers: 'Khách hàng',
    containers: 'Container',
    warehouses: 'Kho hàng',
  }
  return RESOURCE_TO_LABEL[resource] || resource
}
/** Parse an toàn mọi kiểu thường gặp từ API/form (null, string ISO, timestamp số, Date). */
function parseValidDate(value: unknown): Date | null {
  if (value == null || value === '') return null
  if (value instanceof Date) {
    const t = value.getTime()
    return Number.isNaN(t) ? null : new Date(t)
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  if (typeof value === 'string') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  return null
}

/** Ngày cố định: `dd/MM/yyyy`. Luôn truyền giá trị gốc; null/undefined/invalid → `''`. */
export const formatDate = (value?: unknown): string => {
  const date = parseValidDate(value)
  if (!date) return ''
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yyyy = date.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}

/** `dd/MM/yyyy HH:mm` (24h). Chuẩn hiển thị ngày giờ toàn app. Invalid → `''`. */
export function formatDateTime(value?: unknown): string {
  const date = parseValidDate(value)
  if (!date) return ''
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yyyy = date.getFullYear()
  const HH = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  return `${dd}/${mm}/${yyyy} ${HH}:${min}`
}

/**
 * Có giờ thật → `dd/MM/yyyy HH:mm`; chỉ ngày (hoặc midnight UTC date-only) → `dd/MM/yyyy`.
 * Dùng cho công nợ / giao dịch lưu `Date` ngày không có time.
 */
export function formatDateTimeIfHasTime(value?: unknown): string {
  if (value == null || value === '') return ''
  if (typeof value === 'string') {
    const raw = value.trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return formatDate(raw)
    const dateOnlyIso = raw.match(/^(\d{4}-\d{2}-\d{2})T00:00:00(\.\d+)?(Z|[+-]00:00)?$/)
    if (dateOnlyIso) return formatDate(dateOnlyIso[1])
  }
  const date = parseValidDate(value)
  if (!date) return ''
  if (
    date.getHours() === 0 &&
    date.getMinutes() === 0 &&
    date.getSeconds() === 0
  ) {
    return formatDate(date)
  }
  return formatDateTime(date)
}

const pad2Datetime = (n: number) => String(n).padStart(2, '0')

/**
 * Chuỗi local cho ô ngày giờ: `YYYY-MM-DDTHH:mm` (24h).
 * Dùng chung POS / form — không phụ thuộc locale 12h của trình duyệt.
 */
export const toLocalDatetimeInputValue = (d: Date): string =>
  `${d.getFullYear()}-${pad2Datetime(d.getMonth() + 1)}-${pad2Datetime(d.getDate())}T${pad2Datetime(
    d.getHours(),
  )}:${pad2Datetime(d.getMinutes())}`

/** Chuẩn hoá chuỗi giờ về `HH:mm` (24h). Không hợp lệ → `''`. */
export const normalizeTime24h = (raw: string): string => {
  const t = raw.trim()
  const m = t.match(/^(\d{1,2}):(\d{2})$/)
  if (m) {
    const h = Number(m[1])
    const min = Number(m[2])
    if (h >= 0 && h <= 23 && min >= 0 && min <= 59) {
      return `${pad2Datetime(h)}:${pad2Datetime(min)}`
    }
    return ''
  }
  const digits = t.replace(/\D/g, '')
  if (digits.length === 3 || digits.length === 4) {
    const h = Number(digits.length === 3 ? digits.slice(0, 1) : digits.slice(0, 2))
    const min = Number(digits.slice(-2))
    if (h >= 0 && h <= 23 && min >= 0 && min <= 59) {
      return `${pad2Datetime(h)}:${pad2Datetime(min)}`
    }
  }
  return ''
}

/**
 * Removes duplicate objects and null values from an array based on a specified key
 * @param array Array of objects (may contain null values)
 * @param key Property name to check for uniqueness
 * @returns Array of unique objects with null values removed
 */
export function filterUniqueObjects<T extends Record<string, unknown>>(
  array: (T | null | undefined)[],
  key: keyof T = 'id',
): T[] {
  // First, filter out null and undefined values
  const nonNullItems = array.filter((item): item is T => item !== null && item !== undefined)

  // Then remove duplicates by keeping track of keys we've seen
  const uniqueMap = new Map<unknown, T>()

  nonNullItems.forEach((item) => {
    uniqueMap.set(item[key], item)
  })

  // Convert the Map values back to an array
  return Array.from(uniqueMap.values())
}
